import React from "react";
import { withStyles } from "@mui/styles";
import { CircularProgress, Typography } from "@mui/material";
import Button from "../../components/button/Button";
import YesNoDialogModal from "../../components/yesNoDialogModal/YesNoDialogModal";

import {
  clearClubPayment as apiClearClubPayment,
  getPlayerClubPaymentStatus as apiGetPlayerClubPaymentStatus,
} from "../../../services/api";

import { withState } from "../../../services/state";

const styles = (theme) => ({
  container: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  dateText: {
    fontSize: "16px",
  },
  buttonWrapper: {
    width: "200px",
  },
});

function EditBankDetailsForm({ classes, context }) {
  const [gettingPaymentStatus, setGettingPaymentStatus] = React.useState(false);
  const [setupDate, setSetupDate] = React.useState("");
  const [paymentSetup, setPaymentSetup] = React.useState(false);
  const [showClearDirectDebit, setShowClearDirectDebit] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  React.useEffect(() => {
    const fetchStatus = async () => {
      setGettingPaymentStatus(true);
      try {
        const response = await apiGetPlayerClubPaymentStatus(
          context.currentPlayer.id,
          context.currentClub.clubId
        );

        if (response?.success && response.mandateId && response.setupDate) {
          setSetupDate(response.setupDate);
          setPaymentSetup(true);
        } else {
          setPaymentSetup(false);
        }
      } catch (e) {
        console.error("Error fetching payment status", e);
      } finally {
        setGettingPaymentStatus(false);
      }
    };

    fetchStatus();
  }, [context]);

  const setupPayment = async () => {
    setGettingPaymentStatus(true);
    try {
      const response = await apiGetPlayerClubPaymentStatus(
        context.currentPlayer.id,
        context.currentClub.clubId
      );

      if (response?.redirectFlowUrl) {
        window.open(response.redirectFlowUrl, "_blank");
      } else {
        setErrorMessage("Failed to retrieve redirect link");
      }
    } catch (e) {
      console.error("Error setting up payment", e);
      setErrorMessage("Failed to retrieve redirect link");
    } finally {
      setGettingPaymentStatus(false);
    }
  };

  const clearPaymentSetup = async () => {
    setGettingPaymentStatus(true);
    setErrorMessage("");
    try {
      await apiClearClubPayment(
        context.currentPlayer.id,
        context.currentClub.clubId
      );
      setShowClearDirectDebit(false);
      setSetupDate("");
      setPaymentSetup(false);
    } catch (e) {
      console.error("Error clearing payment", e);
      setErrorMessage("An error occurred clearing direct debit");
    } finally {
      setGettingPaymentStatus(false);
    }
  };

  return (
    <div className={classes.container}>
      {gettingPaymentStatus && <CircularProgress />}

      {!gettingPaymentStatus && paymentSetup && (
        <>
          <Typography className={classes.dateText}>
            GoCardless Setup On: {new Date(setupDate).toLocaleDateString()}
          </Typography>
          <div className={classes.buttonWrapper}>
            <Button
              secondary
              onClick={() => setShowClearDirectDebit(true)}
              title="Clear Direct Debit"
            />
          </div>
        </>
      )}

      {!gettingPaymentStatus && !paymentSetup && (
        <>
          <Typography className={classes.dateText}>
            When you click 'Set Up Direct Debit' you will be redirected to
            GoCardless to setup your bank details.
          </Typography>
          <div className={classes.buttonWrapper}>
            <Button
              secondary
              onClick={setupPayment}
              title="Set Up Direct Debit"
            />
          </div>
        </>
      )}

      {errorMessage && <Typography color="error">{errorMessage}</Typography>}

      {showClearDirectDebit && (
        <YesNoDialogModal
          title={`You are about to remove access to your existing direct debit mandate with ${context.currentClub.name}. You will be asked to create a new direct debit mandate via GoCardless next time you come to make a payment. Are you sure you wish to continue?`}
          onClose={() => setShowClearDirectDebit(false)}
          onSubmit={() => clearPaymentSetup()}
          submitting={gettingPaymentStatus}
          error={errorMessage}
        />
      )}
    </div>
  );
}

export default withStyles(styles)(withState(EditBankDetailsForm));
